import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import ReadOnlyField from './readonly-field';
import { Manufacturer } from '../../types';

interface ManufacturerFieldProps<TFieldValues extends FieldValues> {
  /**
   * Field name for the manufacturer ID in the form
   */
  readonly name: Path<TFieldValues>;

  /**
   * Form control from react-hook-form
   */
  readonly control: Control<TFieldValues>;

  /**
   * List of available manufacturers
   */
  readonly manufacturers: Manufacturer[];

  /**
   * Whether the field is in read-only mode
   */
  readonly isReadOnly: boolean;

  /**
   * Whether the field is required (defaults to true)
   */
  readonly required?: boolean;

  /**
   * Function to get the manufacturer name for display in read-only mode
   */
  readonly getManufacturerName: () => string;

  /**
   * Whether the manufacturers are currently loading
   */
  readonly isLoading?: boolean;

  /**
   * Whether the field is disabled (in edit mode)
   */
  readonly disabled?: boolean;

  /**
   * Optional callback for when manufacturer is changed by user
   */
  readonly onManufacturerChange?: (manufacturerId: string) => void;
}

/**
 * A reusable manufacturer field component that can be displayed in both
 * edit mode (dropdown selector) and read-only mode (text display).
 */
const ManufacturerField = <TFieldValues extends FieldValues>({
  name,
  control,
  manufacturers,
  isReadOnly,
  required = true,
  getManufacturerName,
  isLoading = false,
  disabled = false,
  onManufacturerChange = () => {},
}: ManufacturerFieldProps<TFieldValues>) => {
  // Determine the field label based on loading state
  const fieldLabel = isLoading ? 'Loading Manufacturers...' : 'Manufacturer';

  return (
    <Grid item xs={12}>
      {isReadOnly ? (
        <ReadOnlyField label="Manufacturer" value={getManufacturerName()} />
      ) : (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState }) => (
            <FormControl fullWidth error={!!fieldState.error} required={required}>
              <InputLabel id={`${String(name)}-select-label`}>{fieldLabel}</InputLabel>
              <Select
                {...field}
                labelId={`${String(name)}-select-label`}
                label={fieldLabel}
                value={field.value || ''}
                disabled={disabled || isLoading}
                onChange={e => {
                  field.onChange(e);
                  if (onManufacturerChange) {
                    onManufacturerChange(e.target.value);
                  }
                }}
              >
                {manufacturers.map(manufacturer => (
                  <MenuItem key={manufacturer.id} value={manufacturer.id}>
                    {manufacturer.licenseeName}
                  </MenuItem>
                ))}
              </Select>
              {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
            </FormControl>
          )}
        />
      )}
    </Grid>
  );
};

export default ManufacturerField;
